import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vetuvia.com';

const faqItems = [
  {
    question: 'Will this add to my workload?',
    answer:
      'Vetuvia is designed to fit around your existing schedule. You control your availability, rates, and service offerings. Many veterinarians start with just a few hours per week and scale as they build their client base.',
  },
  {
    question: 'Is Vetuvia compliant with veterinary regulations?',
    answer:
      'Yes. Vetuvia is built with veterinary compliance in mind. We support VCPR requirements, state-specific telemedicine regulations, and provide documentation tools to help you maintain proper records. You remain responsible for following your state board guidelines.',
  },
  {
    question: 'How hard is it to set up?',
    answer:
      'Most veterinarians complete their profile setup in under 30 minutes. We provide step-by-step guidance, templates for service descriptions, and support throughout the onboarding process. You can launch your first service within a day.',
  },
  {
    question: 'Who is Vetuvia for?',
    answer:
      "Vetuvia is for employed veterinarians, relief vets, and independent-minded DVMs who want to increase their income and build long-term client relationships without the overhead of opening a full clinic.",
  },
  {
    question: "Can I use Vetuvia if I'm employed full-time?",
    answer:
      "Absolutely. Many of our veterinarians are employed full-time and use Vetuvia to build additional income streams during evenings, weekends, or days off. Check your employment agreement for any restrictions, but most allow outside consulting work.",
  },
];

const organizationNode = {
  '@type': 'Organization',
  '@id': `${siteUrl}/#organization`,
  name: 'Vetuvia',
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: 'https://static.readdy.ai/image/f30444846e2254d95b405efc135ce378/ad77a7a2eb3497b7fdcfdc0de6287849.png',
    width: 512,
    height: 512,
  },
  description:
    'Vetuvia is the recurring income platform for veterinarians. Offer teleconsults, memberships, curated products, and subscription care plans — no clinic required.',
  sameAs: ['https://www.linkedin.com/company/vetuvia'],
};

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Vetuvia',
      description:
        'Vetuvia helps veterinarians create recurring income through telehealth, memberships, curated product sales, and subscription care—all from one platform.',
      publisher: { '@id': `${siteUrl}/#organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/?s={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    organizationNode,
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: siteUrl,
      name: 'Veterinary Telehealth & Commerce Platform | Vetuvia',
      description:
        'Vetuvia helps veterinarians create recurring income through telehealth, memberships, curated product sales, and subscription care—all from one platform.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      dateModified: '2025-07-16',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        ],
      },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${siteUrl}/#app`,
      name: 'Vetuvia',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: siteUrl,
      description:
        'Income infrastructure platform for veterinarians. Create recurring revenue through teleconsults, memberships, curated products, and subscription care plans.',
      offers: [
        {
          '@type': 'Offer',
          name: 'Teleconsults',
          price: '75',
          priceCurrency: 'USD',
          description: 'Veterinary teleconsult sessions at $75–$125 per consult.',
        },
        {
          '@type': 'Offer',
          name: 'Memberships',
          price: '25',
          priceCurrency: 'USD',
          description: 'Monthly membership plans for pet owners at $25/month.',
        },
        {
          '@type': 'Offer',
          name: 'Subscription Care Plans',
          price: '25',
          priceCurrency: 'USD',
          description: 'Subscription care plans for ongoing pet health at $25/month.',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${siteUrl}/#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ],
};

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Vetuvia',
    },
    {
      ...organizationNode,
      description:
        'Vetuvia believes great veterinarians make the world better. We build modern, veterinarian-led services that improve access to care and help veterinarians expand their impact.',
      foundingDescription:
        'Vetuvia was created to bridge the gap between pet owners who need timely trusted support and veterinarians who want more flexibility, ownership, and ways to serve beyond the traditional clinic model.',
    },
    {
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#webpage`,
      url: `${siteUrl}/about`,
      name: 'About Vetuvia – Honoring Great Veterinarians | Mission & Values',
      description:
        'Vetuvia was built to honor great veterinarians and expand their impact. Learn about our mission to make veterinary care more accessible, our core values, and why we believe passionate veterinarians can change the world.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      dateModified: '2025-07-16',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
        ],
      },
    },
  ],
};

const pricingJsonLd = (
  tiers: Array<{ name: string; price: string; description: string }>
) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Vetuvia',
    },
    organizationNode,
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/pricing#webpage`,
      url: `${siteUrl}/pricing`,
      name: 'Pricing Plans – Vetuvia | Flexible Plans for Veterinarian Income Growth',
      description:
        'Choose the perfect Vetuvia plan for your veterinary practice. Transparent pricing for teleconsults, memberships, and subscription care plans. Start free, no hidden fees.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      dateModified: '2025-07-16',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${siteUrl}/pricing` },
        ],
      },
    },
    {
      '@type': 'Product',
      '@id': `${siteUrl}/pricing#product`,
      name: 'Vetuvia Platform',
      description: 'Income infrastructure platform for veterinarians — teleconsults, memberships, curated products, and subscription care plans.',
      brand: { '@id': `${siteUrl}/#organization` },
      offers: tiers.map((tier) => ({
        '@type': 'Offer',
        name: `Vetuvia ${tier.name} Plan`,
        price: tier.price === 'Free' ? '0' : tier.price === 'Custom' ? '0' : tier.price.replace('$', ''),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: `${siteUrl}/pricing`,
        description: tier.description,
      })),
    },
    {
      '@type': 'ItemList',
      '@id': `${siteUrl}/pricing#plans`,
      name: 'Vetuvia Pricing Plans',
      itemListElement: tiers.map((tier, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `Vetuvia ${tier.name} Plan`,
        description: tier.description,
        url: `${siteUrl}/pricing`,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${siteUrl}/pricing#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Does Vetuvia take a cut of my service revenue?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Vetuvia takes 0% commission on veterinary services — teleconsults, memberships, and any service you deliver directly. Revenue sharing applies only to optional curated products and platform-enabled programs.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I upgrade or downgrade my plan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. You can change your plan at any time. Changes take effect at the start of your next billing cycle.',
          },
        },
        {
          '@type': 'Question',
          name: 'What counts as a "veterinary service"?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Any service you deliver as a licensed veterinarian — teleconsults, advice sessions, memberships, and care plans. Product sales and platform-enabled programs have separate margin structures.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is there a free trial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Vetuvia is currently invite-only. Request an invitation and our team will be in touch with onboarding details.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I switch plans at any time?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges or credits.",
          },
        },
        {
          '@type': 'Question',
          name: 'Is there a setup fee?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No setup fees, ever. You only pay the monthly subscription fee for your chosen plan.',
          },
        },
      ],
    },
  ],
});

interface SEOJsonLdProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schemaType?: 'WebPage' | 'AboutPage' | 'PricingPage';
  additionalSchema?: Record<string, unknown>;
  pricingTiers?: Array<{ name: string; price: string; description: string }>;
}

export default function SEOJsonLd({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schemaType,
  additionalSchema,
  pricingTiers,
}: SEOJsonLdProps) {
  useEffect(() => {
    // Use a unique script ID per schema type to avoid collisions across pages
    const scriptId =
      schemaType === 'AboutPage'
        ? 'vetuvia-jsonld-about'
        : schemaType === 'PricingPage'
        ? 'vetuvia-jsonld-pricing'
        : 'vetuvia-jsonld-home';

    // Remove any stale JSON-LD scripts from previous page renders
    document.querySelectorAll('script[id^="vetuvia-jsonld"]').forEach((el) => el.remove());

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';

    if (additionalSchema) {
      script.textContent = JSON.stringify(additionalSchema);
    } else if (schemaType === 'AboutPage') {
      script.textContent = JSON.stringify(aboutJsonLd);
    } else if (schemaType === 'PricingPage' && pricingTiers) {
      script.textContent = JSON.stringify(pricingJsonLd(pricingTiers));
    } else {
      script.textContent = JSON.stringify(homeJsonLd);
    }
    document.head.appendChild(script);

    // Update <title>
    if (title) document.title = title;

    // Helper: upsert a meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^\]]+)="([^\]]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);
    if (canonical) {
      setMeta('meta[property="og:url"]', 'content', canonical);
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', 'content', ogImage);
      setMeta('meta[name="twitter:image"]', 'content', ogImage);
    }

    return () => {
      document.querySelectorAll('script[id^="vetuvia-jsonld"]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogImage, schemaType, additionalSchema, pricingTiers]);

  return null;
}
